import {
  Checkbox,
  CheckboxGroup,
  ComponentMultiStyleConfig,
  Input,
  Stack,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../lib/util/common-util";

/**
 * チェックボックス表示用のデータクラス
 */
export type CheckboxContent = {
  options: CheckboxOption[];
};

export type CheckboxOption = {
  label: string;
  value: string;
};

export type CMFormInputBaseCheckboxProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "diff"; // エラーモードは非対応。
  size?: "sm" | "md" | "lg";

  /**
   * チェックボックスの表示方向。
   */
  direction?: "row" | "column";

  /**
   * trueなら、readOnlyモードのときに縦幅を縮める。
   */
  shrinkOnReadOnly?: boolean;

  /**
   * チェックボックス表示用のデータ。
   */
  content: CheckboxContent;

  /**
   * 値。
   */
  values?: string[];

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: string[] | null;

  onChange?: (selectedValues: string[]) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * Checkboxコンポーネント.
 *
 * 差分モードでは、変更後の選択肢に枠を表示する。
 * エラーモード非対応。
 */
export const CMFormInputBaseCheckbox = ({
  editMode = "editable",
  variant = "default",
  size = "md",
  direction = "column",
  shrinkOnReadOnly = false,

  content,
  values = [],
  changedFrom,

  onChange,
  onBlur,

  sx,
  ...rest
}: CMFormInputBaseCheckboxProps) => {
  const styles = useMultiStyleConfig("CMFormInputBaseCheckboxStyles", {
    variant,
    size,
  });

  const disabled = useMemo(
    () => editMode === "disabled" || editMode === "readOnly",
    [editMode],
  );
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const _changedFrom = useMemo(() => changedFrom ?? [], [changedFrom]);

  // 参照用Inputエリアのスタイル設定
  const readonlyLabelStyles = useMemo(
    () => ({
      ...(editMode === "readOnly" ? styles.labelReadOnly : undefined),
      ...(editMode === "readOnly" && shrinkOnReadOnly
        ? styles.labelReadOnlyShrunk
        : undefined),
    }),
    [styles, editMode, shrinkOnReadOnly],
  );

  const itemStyles = useMemo(
    () => ({
      ...styles.optionText,
      ...(editMode === "disabled" ? styles.optionTextDisabled : undefined),
    }),
    [styles, editMode],
  );

  /**
   * 差分モードで、値が変更された場合に適応するスタイルの設定。
   * 対象：変更後の項目
   */
  const diffItemStyles = useMemo(
    () => ({
      ...styles.optionText,
      ...(variant === "diff" ? styles.selectDiffValue : undefined),
    }),
    [styles, variant],
  );

  /**
   * 差分モード時、値のチェック状態が変更前と異なるならtrueを返す。
   * 以下のいずれかの場合はfalseを返す。
   * - 差分モードではない。
   * - 変更前とチェック状態が同一である。
   *
   * @param v 判定対象の値。
   * @return 異なる場合はtrue。
   */
  const hasChangedInDiffMode: (v: string) => boolean = (v) => {
    if (!isDiffMode) return false;

    const isCheckedBefore = _changedFrom.includes(v);
    const isCheckedNow = values.includes(v);

    return isCheckedBefore !== isCheckedNow;
  };

  /**
   * 値に対応するラベルを取得する。
   * @param value 値
   * @return ラベル
   */
  const labelOf = (value: string) => {
    const option = content.options.find((o) => o.value === value);
    return hasValue(option) ? option.label : "";
  };

  /**
   * CheckboxGroupの入力値を取得し、呼び出し元に返却
   * @param values 入力値
   */
  const handleChange = (values: (string | number)[]) => {
    onChange?.(values.map((v) => v.toString()));
  };

  return (
    <>
      {editMode === "readOnly" ? (
        <Stack direction={direction}>
          {values.map((value, index) => (
            <Input
              disabled={disabled}
              key={index}
              value={labelOf(value)}
              sx={{ ...readonlyLabelStyles, ...sx }}
              {...rest}
            />
          ))}
        </Stack>
      ) : (
        <CheckboxGroup
          defaultValue={values}
          value={values}
          onChange={handleChange}
        >
          <Stack
            direction={direction}
            sx={{ ...styles.container, ...sx }}
            {...rest}
          >
            {content.options.map((option, index) => (
              <Checkbox
                isDisabled={disabled}
                key={index}
                value={option.value}
                marginTop={"0px"}
                onBlur={onBlur}
              >
                {hasChangedInDiffMode(option.value) ? (
                  <Text sx={diffItemStyles}>{option.label}</Text>
                ) : (
                  <Text sx={itemStyles}>{option.label}</Text>
                )}
              </Checkbox>
            ))}
          </Stack>
        </CheckboxGroup>
      )}
    </>
  );
};

export const CMFormInputBaseCheckboxStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "optionText",
    "optionTextDisabled",
    "selectDiffValue",
    "checkbox",
    "labelReadOnly",
    "labelReadOnlyShrunk",
  ],
  baseStyle: {
    container: {
      alignItems: "flex-start",
    },
    optionText: {
      textAlign: "start",
    },
    optionTextDisabled: {},
    labelReadOnly: {
      cursor: "default",
      _disabled: {
        opacity: 1,
      },
    },
    labelReadOnlyShrunk: {},
  },
  variants: {
    default: {
      optionText: {
        // textAlign: "center",
      },

      selectDiffValue: {},
      labelReadOnlyBase: {},
      labelReadOnly: {
        border: "0",
      },
      labelReadOnlyShrunk: {},
    },
    error: {
      optionText: {
        borderWidth: "1px",
        borderColor: "transparent",
        // textAlign: "center",
      },
      selectDiffValue: {
        borderWidth: "1px",
        borderColor: "red.500",
        borderRadius: "4px",
      },
      labelReadOnlyBase: {},
      labelReadOnly: {},
      labelReadOnlyShrunk: {},
    },
    diff: {
      optionText: {
        borderWidth: "1px",
        borderColor: "transparent",
        // textAlign: "center",
      },
      selectDiffValue: {
        borderWidth: "1px",
        borderColor: "orange.500",
        borderRadius: "4px",
      },
      labelReadOnlyBase: {
        borderWidth: "1px",
        borderColor: "orange.500",
        _hover: {
          borderColor: "orange.500",
        },
        _focus: {
          _hover: {
            borderColor: "blue.500",
          },
        },
      },
      labelReadOnly: {},
      labelReadOnlyShrunk: {},
    },
  },
  sizes: {
    sm: {
      optionText: {
        // h: "22px",
        px: "6px",
        lineHeight: "22px",
      },
      checkbox: {
        h: "14px",
      },
      labelReadOnlyBase: {
        // h: "22px",
        px: "6px",
      },
      labelReadOnly: {},
      labelReadOnlyShrunk: {
        // h: "18px",
      },
    },
    md: {
      optionText: {
        // h: "24px",
        px: "8px",
        lineHeight: "24px",
      },
      checkbox: {},
      labelReadOnlyBase: {
        // h: "30px",
        px: "8px",
      },
      labelReadOnly: {},
      labelReadOnlyShrunk: {
        // h: "24px",
      },
    },
    lg: {
      optionText: {
        // h: "38px",
        px: "10px",
        lineHeight: "38px",
      },
      checkbox: {
        h: "20px",
      },
      labelReadOnlyBase: {
        // h: "38px",
        px: "10px",
      },
      labelReadOnly: {},
      labelReadOnlyShrunk: {
        // h: "30px",
      },
    },
  },
};
